import React, { Fragment } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
  Labeled,
  Show,
  TabbedShowLayout,
  Tab,
  DateField,
  NumberField,
  ReferenceField,
  ReferenceManyField,
  TextField,
  SelectField,
  BooleanField,
  Datagrid,
  FunctionField,
  ListButton,
} from 'react-admin';
import CardActions from '@material-ui/core/CardActions';
import FullNameField from '../General/FullNameField';
import EditButton from './EditButton';
import SubmitToFolioButton from './SubmitToFolioButton';

import { getAdminLevel, ADMIN_LEVELS } from '../../authProvider';
import ParsedNumberField from '../ParsedNumberField';
import CancelOrderButton from './CancelOrderButton';
import { offeringCategory, annualIncomeChoices, netWorthChoices } from '../../helpers/mappings';


const Title = ({ record }) => (
  <span>Offer Order #{record.id}</span>
);


const cardActionStyle = {
  zIndex: 2,
  display: 'inline-block',
  float: 'right',
};

const conditionalOfferingStyles = {
  conditionalInsideRange: { color: 'green' },
  conditionalOutsideRange: { color: 'red' },
  nonConditional: { color: 'black', display: 'none' },
};

const Actions = ({
  basePath, data, path, resource,
}) => {
  const adminLevel = getAdminLevel();
  return (
    <CardActions style={cardActionStyle}>
      { adminLevel && adminLevel !== ADMIN_LEVELS.READ_ONLY_ADMIN ?
        <EditButton record={data} basePath={basePath} path={path} /> : undefined
      }
      {
        data && adminLevel && adminLevel !== ADMIN_LEVELS.READ_ONLY_ADMIN ?
          <Fragment>
            <SubmitToFolioButton resource={resource} record={data} />
            <CancelOrderButton resource={resource} record={data} />
          </Fragment> : undefined
      }
      <ListButton basePath={basePath} />
    </CardActions>
  );
};

const ConditionalOfferElligibilityField = withStyles(conditionalOfferingStyles)(({ classes, ...props }) => {
  if (props.record.offer) {
    const {
      conditionalMinSharePrice: minSharePrice,
      conditionalMaxSharePrice: maxSharePrice,
      isConditional,
    } = props.record;
    const { pricePerShare } = props.record.offer;
    const isInsideRange = (minSharePrice <= pricePerShare && pricePerShare <= maxSharePrice);
    let className = 'nonConditional';
    let text = '';
    if (isConditional) {
      if (isInsideRange) {
        className = 'conditionalInsideRange'; text = 'Yes';
      } else {
        className = 'conditionalOutsideRange'; text = 'No';
      }
    }
    return (
      <Labeled label="Eligibile for Submission">
        <FunctionField
          className={classes[className]}
          render={() => `${text}`}
        />
      </Labeled>
    );
  }
  return null;
});

export default props => (
  <Show
    {...props}
    actions={<Actions path={props.location.pathname} />}
    title={<Title />}
  >
    <TabbedShowLayout>
      <Tab label="Offer Order Details">
        <DateField source="createdAt" />
        <DateField source="updatedAt" />
        <DateField source="folioPrivatePlacementOrderCreatedAt" />
        <TextField source="folioPrivatePlacementOrderOid" />
        <TextField label="Name" source="offer.name" />
        <FunctionField
          label="Offer Category"
          render={record => `${record.isConditional ? offeringCategory.CONDITIONAL.label : offeringCategory.NORMAL.label}`}
        />
        <ConditionalOfferElligibilityField />
        <ReferenceField label="User" source="userId" reference="users" linkType="show">
          <FullNameField />
        </ReferenceField>
        <ReferenceField label="Account" source="accountNumber" reference="accounts" linkType={false}>
          <TextField source="accountNumber" />
        </ReferenceField>
        <ReferenceField label="Solicited By" source="solicitedBy" reference="users" linkType="show">
          <FullNameField />
        </ReferenceField>
        <TextField source="status" />
        <NumberField source="requestedTotalCurrencyAmt" options={{ style: 'currency', currency: 'USD' }} />
        <ReferenceField label="Created by" source="createdByUserId" reference="users" linkType="show">
          <FullNameField />
        </ReferenceField>
      </Tab>
      <Tab label="User Info">
        <ReferenceField label="Employment Status" source="userId" reference="users" linkType={false}>
          <TextField source="employment.employmentStatus" label="Employment Status" />
        </ReferenceField>
        <ReferenceField label="Employer Name" source="userId" reference="users" linkType={false}>
          <TextField source="employment.employerName" label="Employer Name" />
        </ReferenceField>
        <ReferenceField label="Annual Income" source="userId" reference="users" linkType={false}>
          <SelectField source="finances.annualIncome" label="Annual Income" choices={annualIncomeChoices} />
        </ReferenceField>
        <ReferenceField label="Liquid Net Worth" source="userId" reference="users" linkType={false}>
          <SelectField source="finances.liquidNetWorth" label="Liquid Net Worth" choices={netWorthChoices} />
        </ReferenceField>
        <ReferenceField label="Total Net Worth" source="userId" reference="users" linkType={false}>
          <SelectField source="finances.totalNetWorth" label="Total Net Worth" choices={netWorthChoices} />
        </ReferenceField>
        <ReferenceField label="Finra Affiliated" source="userId" reference="users" linkType={false}>
          <BooleanField source="disclosures.finraAffiliated" label="FINRA Affiliated" />
        </ReferenceField>
        <ReferenceField label="Director Or Ten Percent Shareholder" source="userId" reference="users" linkType={false}>
          <BooleanField source="disclosures.directorOrTenPercentShareholder" label="Director or 10% Shareholder" />
        </ReferenceField>
        <ReferenceField label="End Market Value" reference="accountPerformanceSummary" source="accountNumber" linkType={false}>
          <ParsedNumberField
            source="endMarketValue"
            options={{ style: 'currency', currency: 'USD' }}
          />
        </ReferenceField>
        <ReferenceField label="Cash Balance" reference="accountCashBalance" source="accountNumber" linkType={false}>
          <ParsedNumberField
            source="balanceAvailableForT1Trades"
            options={{ style: 'currency', currency: 'USD' }}
          />
        </ReferenceField>
        <ReferenceManyField label="Positions" reference="accountPositions" source="accountNumber" target="accountNumber" sortable={false}>
          <Datagrid>
            <TextField source="security.companyName" label="Company Name" />
            <TextField source="security.ticker" label="Ticker" />
            <TextField source="shareQuantity" label="Share Quantity" />
            <TextField source="currentWeight" label="Current Weight" />
            <TextField source="shortTermCostBasis" label="Short Term Cost" />
            <TextField source="longTermCostBasis" label="Long Term Cost" />

            <TextField source="security.instrumentType" label="Instrument Type" />
            <TextField source="security.settlementPeriod" label="Settlement Period" />
          </Datagrid>
        </ReferenceManyField>
        <ReferenceManyField label="Transactions" reference="accountTransactions" source="accountNumber" target="accountNumber" sortable={false}>
          <Datagrid>
            <DateField source="date" sortable={false} />
            <TextField source="transactionDesc" sortable={false} />
            <TextField source="ticker" sortable={false} />
            <TextField source="symbolName" sortable={false} />
            <ParsedNumberField source="quantity" sortable={false} />
            <ParsedNumberField source="price" sortable={false} />
          </Datagrid>
        </ReferenceManyField>
      </Tab>
    </TabbedShowLayout>
  </Show>
);



// WEBPACK FOOTER //
// ./src/components/OfferOrder/Show.js